import { initializeApp, getApps } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getDatabase, connectDatabaseEmulator } from "firebase/database";
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, connectAuthEmulator } from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  uploadString,
  getDownloadURL,
  uploadBytesResumable,
  connectStorageEmulator,
} from "firebase/storage";
import { emulatorConfig, devQueryFlag, DEV_SESSION_KEY } from "./firebaseEmulators";

const firebaseConfig = {
  apiKey: "AIzaSyAbRAOe2kewcrRYLyDPgyoUfRckIWNAzF8",
  authDomain: "restauradmin.firebaseapp.com",
  databaseURL: "https://restauradmin-default-rtdb.firebaseio.com",
  projectId: "restauradmin",
  storageBucket: "restauradmin.appspot.com",
  messagingSenderId: "197138197382",
  appId: "1:197138197382:web:f3746bfbf1201451b25a07",
  measurementId: "G-69G1V4WFYQ",
};

// EMULADORES (solo pruebas en la compu): null en producción SIEMPRE — la decisión y sus
// candados están en firebaseEmulators.js, con tests. Con null, este archivo hace exactamente lo
// de siempre (ningún connect*, App Check igual que antes).
function readEmulatorConfig() {
  if (typeof window === "undefined" || !window.location) return null;
  const { hostname, port, search } = window.location;
  let sessionFlag = null;
  // Solo en `npm start`: ?emu=1 queda recordado en la pestaña (navegar dentro de la app pierde la
  // query) y ?emu=0 lo olvida. En un build de producción este bloque no existe (NODE_ENV fijo).
  if (process.env.NODE_ENV === "development") {
    try {
      const q = devQueryFlag(search);
      if (q === "on") sessionStorage.setItem(DEV_SESSION_KEY, "1");
      else if (q === "off") sessionStorage.removeItem(DEV_SESSION_KEY);
      sessionFlag = sessionStorage.getItem(DEV_SESSION_KEY);
    } catch (_) {
      /* sin sessionStorage: vale solo el ?emu=1 de esta carga */
    }
  }
  return emulatorConfig({
    hostname,
    port,
    search,
    sessionFlag,
    buildFlag: process.env.REACT_APP_USE_EMULATORS,
    nodeEnv: process.env.NODE_ENV,
  });
}
const EMU = readEmulatorConfig();

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Firestore con persistencia offline (IndexedDB, multi-pestaña). Nativa y
// gratis: la vista de servicio aguanta quedarse sin internet — lee de cache y
// encola las escrituras hasta reconectar. Defensivo ante hot-reload de CRA
// (initializeFirestore solo puede llamarse una vez por app).
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
} catch (e) {
  db = getFirestore(app);
}
const firestore = db;
const storage = getStorage(app);

// Realtime Database: SOLO se usa como "timbre" (doorbell) del agente de impresión.
// Al encolar un print_job, la web escribe un timestamp en /wake/{uid}/{menuId}; el
// agente local mantiene un listener ahí y así imprime en <1s sin pollear. El
// contenido del ticket NUNCA pasa por RTDB (solo un número), y App Check de RTDB
// queda SIN enforce (el agente Node no puede attestar) — no afecta el App Check de
// Firestore, que es un toggle aparte por producto. Ver comandasFirestoreService.
const rtdb = getDatabase(app);

// App SECUNDARIA aislada para el widget público de reservas (/book/:slug).
// Su login anónimo NO debe tocar la sesión del dueño en la app principal
// (si la tocara, al dueño se le cae la sesión: desaparece la feature y fallan
// los guardados por permisos). Instancia + auth + db propios.
const publicApp =
  getApps().find((a) => a.name === "publicBooking") ||
  initializeApp(firebaseConfig, "publicBooking");
const publicAuth = getAuth(publicApp);
const publicDb = getFirestore(publicApp);

// Con los emuladores, TODO lo de las dos apps va a la compu (antes de cualquier uso: estos
// connect* tienen que correr antes de la primera lectura/escritura, y este módulo es lo primero
// que se evalúa). Firestore conserva initializeFirestore y su caché persistente: la POS depende
// de `fromCache` (ORDERS_STALE) y del modo sin internet, y así se prueba igual que en la nube.
// try/catch: con el hot-reload de `npm start` la instancia ya está arrancada y conectada.
if (EMU) {
  const { host, ports } = EMU;
  const tryConnect = (what, fn) => {
    try {
      fn();
    } catch (e) {
      console.warn(`[emuladores] ${what}:`, (e && e.message) || e);
    }
  };
  for (const a of [auth, publicAuth]) tryConnect("auth", () => connectAuthEmulator(a, `http://${host}:${ports.auth}`, { disableWarnings: true }));
  for (const d of [db, publicDb]) tryConnect("firestore", () => connectFirestoreEmulator(d, host, ports.firestore));
  tryConnect("database", () => connectDatabaseEmulator(rtdb, host, ports.database));
  tryConnect("storage", () => connectStorageEmulator(storage, host, ports.storage));
  console.warn(`[emuladores] Firebase apunta a los EMULADORES de ${host} (no a producción).`);
  try {
    // Una marca que no mueve el layout (las capturas miden la pantalla): el título de la pestaña.
    if (typeof document !== "undefined" && !/^\[EMU\]/.test(document.title)) document.title = `[EMU] ${document.title}`;
  } catch (_) {
    /* sin document */
  }
}

// ── App Check (anti-abuso / anti-spam de reservas anónimas) ──
// INERTE hasta que definas REACT_APP_APPCHECK_SITE_KEY (clave reCAPTCHA v3).
// Pasos: (1) Firebase console → App Check → registrar la app web con reCAPTCHA v3
// (te da un site key); (2) poné REACT_APP_APPCHECK_SITE_KEY=<site key> en el
// build; (3) cuando verifiques que los tokens fluyen, activá ENFORCEMENT en
// Firestore y Functions desde la consola. En localhost, para testear, definí
// REACT_APP_APPCHECK_DEBUG con el debug token (o "true" y copiá el de la consola).
const APPCHECK_SITE_KEY = process.env.REACT_APP_APPCHECK_SITE_KEY;
// Con los emuladores no hay App Check (no lo validan, y reCAPTCHA saldría a internet).
if (typeof window !== "undefined" && APPCHECK_SITE_KEY && !EMU) {
  if (process.env.REACT_APP_APPCHECK_DEBUG) {
    // eslint-disable-next-line no-restricted-globals
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = process.env.REACT_APP_APPCHECK_DEBUG;
  }
  try {
    const provider = new ReCaptchaV3Provider(APPCHECK_SITE_KEY);
    initializeAppCheck(app, { provider, isTokenAutoRefreshEnabled: true });
    // El widget público usa la app aislada: también necesita App Check para que
    // sus escrituras (reservas anónimas) pasen el enforcement.
    initializeAppCheck(publicApp, { provider, isTokenAutoRefreshEnabled: true });
  } catch (e) {
    console.warn("App Check init failed:", e);
  }
}

// Functions (para la callable de reserva pública transaccional). Región default
// us-central1 (igual que renderMenuPdf).
const functions = getFunctions(app);
const publicFunctions = getFunctions(publicApp);
if (EMU) {
  for (const f of [functions, publicFunctions]) {
    try {
      connectFunctionsEmulator(f, EMU.host, EMU.ports.functions);
    } catch (e) {
      console.warn("[emuladores] functions:", (e && e.message) || e);
    }
  }
}

export {
  app,
  auth,
  firestore,
  EmailAuthProvider,
  reauthenticateWithCredential,
  db,
  rtdb,
  publicAuth,
  publicDb,
  functions,
  publicFunctions,
  storage,
  ref,
  uploadBytes,
  uploadString,
  getDownloadURL,
  uploadBytesResumable,
};
